import "@coreui/coreui/dist/css/coreui.min.css";
import React, { useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Bell, ChevronDown, LogOut, Menu, Search, ShieldCheck, UserRound } from "lucide-react";
import {
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CFormInput,
  CNav,
  CNavItem,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
} from "@coreui/react";
import AppearanceToggle from "../AppearanceToggle";
import {
  adminNavigationSections,
  flattenAdminNavigationSections,
} from "../../config/adminNavigation";
import { getDashboardCssVariables } from "../../theme/dashboardTokens";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { clearStoredAuth } from "../../utils/authSession";
import { useAppearance } from "../../context/AppearanceContext";

function getIdentity() {
  const userid = localStorage.getItem("userid") || "Admin";
  const firstname = localStorage.getItem("firstname") || "";
  const lastname = localStorage.getItem("lastname") || "";
  const displayName = [firstname, lastname].filter(Boolean).join(" ").trim() || userid;
  return { userid, displayName };
}

function renderNavIcon(item) {
  if (!item?.icon) return null;
  const Icon = item.icon;
  return <Icon size={18} strokeWidth={2} />;
}

function resolveIsActive(item, location) {
  if (!item) return false;
  if (typeof item.match === "function") return item.match(location);
  return location.pathname === item.href || location.pathname.startsWith(item.href);
}

export default function AdminShell({
  title,
  subtitle,
  badge = "Experts4Europe Admin",
  navItems = null,
  navSections = null,
  actions = null,
  shellLabel = "AI Operations Room",
  shellDescription = "CoreUI React bildet Shell, Layout und Arbeitsflaechen fuer operative Admin-Seiten.",
  modeLabel = "Review First",
  liveLabel = "Admin",
  utilityHref = null,
  utilityLabel = "Konto",
  topSignal = "Experts4Europe",
  topContext = "Admin Control",
  workspaceTitle = "Experts4Europe Admin",
  workspaceNotice = "Alle Bereiche laufen jetzt in derselben CoreUI-React-Arbeitsflaeche.",
  searchTitle = "Schnellzugriff",
  searchDescription = "Suche nach Bereichen, Untermenues und operativen Wegen.",
  searchPlaceholder = "Bereich, Aktion, Untermenue ...",
  searchEmptyText = "Keine passende Aktion gefunden.",
  profileLinks = null,
  searchItems = [],
  notifications = [],
  family = "admin",
  pageClassName = "",
  contentClassName = "",
  children,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { resolvedMode } = useAppearance();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const identity = useMemo(() => getIdentity(), []);
  const cssVariables = useMemo(
    () => getDashboardCssVariables({ family, appearance: resolvedMode }),
    [family, resolvedMode]
  );

  const resolvedSections = useMemo(() => {
    if (Array.isArray(navSections) && navSections.length) return navSections;
    if (Array.isArray(navItems) && navItems.length) {
      return [{ key: "default", label: "Admin", items: navItems }];
    }
    return adminNavigationSections;
  }, [navItems, navSections]);

  const flatNavigation = useMemo(
    () => flattenAdminNavigationSections(resolvedSections),
    [resolvedSections]
  );
  const activeItem = flatNavigation.find((item) => resolveIsActive(item, location));

  const searchCatalog = useMemo(() => {
    const byKey = new Map();
    [...flatNavigation, ...(searchItems || [])].forEach((item) => {
      if (!item?.href || !item?.label) return;
      const key = `${item.href}:${item.label}`;
      if (!byKey.has(key)) byKey.set(key, item);
    });
    return Array.from(byKey.values());
  }, [flatNavigation, searchItems]);

  const filteredSearchItems = searchCatalog.filter((item) => {
    const haystack = `${item.label} ${item.description || ""}`.toLowerCase();
    return haystack.includes(searchQuery.trim().toLowerCase());
  });

  const visibleNotifications = notifications.length
    ? notifications
    : [{
        key: "workspace",
        title: activeItem?.label || title || badge,
        text: workspaceNotice,
        href: location.pathname + location.search,
      }];

  const resolvedProfileLinks = profileLinks?.length
    ? profileLinks
    : [
        { href: "/dashboard/admin", label: "Command" },
        { href: "/dashboard/admin/users", label: "User Management" },
      ];

  const goTo = (href) => {
    setMobileNavOpen(false);
    setSearchOpen(false);
    setSearchQuery("");
    navigate(href);
  };

  const handleLogout = () => {
    clearStoredAuth();
    window.location.assign("/login");
  };

  const renderNavigation = () => (
    <div className="admin-core-rail-stack">
      <div className="admin-core-brand">
        <div className="admin-core-brand__mark" aria-hidden="true">
          <span>E4E</span>
        </div>
        <div className="admin-core-brand__copy">
          <p className="admin-core-brand__name">Experts4Europe</p>
          <p className="admin-core-brand__meta">{shellLabel}</p>
        </div>
      </div>

      <div className="admin-core-rail-panel">
        <div className="admin-core-rail-panel__meta">
          <span className="admin-core-kicker">{liveLabel}</span>
          <CBadge className="admin-core-badge admin-core-badge--muted">{modeLabel}</CBadge>
        </div>
        <p className="admin-core-rail-panel__title">{badge}</p>
        <p className="admin-core-rail-panel__copy">{shellDescription}</p>
      </div>

      <div className="admin-core-nav-wrap">
        {resolvedSections.map((section) => (
          <div key={section.key || section.label} className="admin-core-nav-group">
            {section.label ? <span className="admin-core-nav-group__label">{section.label}</span> : null}
            <CNav className="admin-core-nav" role="navigation">
              {(section.items || []).map((item) => {
                const isActive = resolveIsActive(item, location);
                return (
                  <CNavItem key={item.href}>
                    <NavLink
                      to={item.href}
                      onClick={() => setMobileNavOpen(false)}
                      className={`admin-core-nav__item${isActive ? " is-active" : ""}`}
                    >
                      <span className="admin-core-nav__icon">{renderNavIcon(item)}</span>
                      <span>{item.label}</span>
                    </NavLink>
                  </CNavItem>
                );
              })}
            </CNav>
          </div>
        ))}
      </div>

      <div className="admin-core-rail-footer">
        {utilityHref ? (
          <CButton
            color="light"
            variant="outline"
            className="admin-core-button admin-core-button--secondary"
            onClick={() => goTo(utilityHref)}
          >
            <ShieldCheck size={16} strokeWidth={2} />
            <span>{utilityLabel}</span>
          </CButton>
        ) : null}
        <CButton
          color="light"
          variant="outline"
          className="admin-core-button admin-core-button--secondary"
          onClick={() => navigate("/")}
        >
          <ArrowLeft size={16} strokeWidth={2} />
          <span>Zur Website</span>
        </CButton>
      </div>
    </div>
  );

  return (
    <div className="admin-core-shell" style={cssVariables}>
      <div className="admin-core-grid">
        <aside className="admin-core-sidebar">
          <CCard className="admin-core-sidebar-card">
            <CCardBody className="admin-core-sidebar-card__body">
              {renderNavigation()}
            </CCardBody>
          </CCard>
        </aside>

        <main className={`admin-core-main ${pageClassName}`.trim()}>
          <header className="admin-core-topbar">
            <div className="admin-core-topbar__leading">
              <CButton
                color="light"
                variant="ghost"
                className="admin-core-icon-button admin-core-icon-button--menu"
                aria-label="Navigation oeffnen"
                onClick={() => setMobileNavOpen(true)}
              >
                <Menu size={18} strokeWidth={2} />
              </CButton>

              <div className="admin-core-topbar__panel">
                <span className="admin-core-kicker">{topSignal}</span>
                <strong>{topContext}</strong>
              </div>
            </div>

            <div className="admin-core-topbar__panel admin-core-topbar__panel--center">
              <span className="admin-core-kicker">Fokus</span>
              <strong>{title || activeItem?.label || "Admin"}</strong>
            </div>

            <div className="admin-core-topbar__actions">
              <CButton
                color="light"
                variant="ghost"
                className="admin-core-icon-button"
                aria-label="Suche oeffnen"
                onClick={() => setSearchOpen(true)}
              >
                <Search size={16} strokeWidth={2} />
              </CButton>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="admin-core-icon-button" aria-label="Hinweise">
                    <Bell size={16} strokeWidth={2} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="admin-core-menu">
                  <DropdownMenuLabel>Hinweise</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {visibleNotifications.map((item) => (
                    <DropdownMenuItem key={item.key || item.href} onSelect={() => goTo(item.href)}>
                      <div className="admin-core-menu__item">
                        <strong>{item.title}</strong>
                        <span>{item.text}</span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <AppearanceToggle compact />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="admin-core-profile" aria-label="Profilmenue">
                    <div className="admin-core-profile__avatar">
                      <UserRound size={16} strokeWidth={2} />
                    </div>
                    <div className="admin-core-profile__copy">
                      <strong>{identity.displayName}</strong>
                      <span>{identity.userid}</span>
                    </div>
                    <ChevronDown size={14} strokeWidth={2} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="admin-core-menu">
                  <DropdownMenuLabel>Profilmenue</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {resolvedProfileLinks.map((item) => (
                    <DropdownMenuItem key={item.href} onSelect={() => goTo(item.href)}>
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                  {utilityHref && !resolvedProfileLinks.some((item) => item.href === utilityHref) ? (
                    <DropdownMenuItem onSelect={() => goTo(utilityHref)}>{utilityLabel}</DropdownMenuItem>
                  ) : null}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={handleLogout}>
                    <span className="admin-core-menu__logout">
                      <LogOut size={15} strokeWidth={2} />
                      <span>Abmelden</span>
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          <CCard className="admin-core-hero">
            <CCardBody className="admin-core-hero__body">
              <div className="admin-core-hero__copy">
                <div className="admin-core-hero__meta">
                  <span className="admin-core-kicker">{shellLabel}</span>
                  <CBadge className="admin-core-badge">{activeItem?.label || liveLabel}</CBadge>
                </div>
                <h1>{title || activeItem?.label || "Admin"}</h1>
                {subtitle ? <p>{subtitle}</p> : null}
              </div>
              {actions ? <div className="admin-core-hero__actions">{actions}</div> : null}
            </CCardBody>
          </CCard>

          <section className={`admin-core-content admin-shell-legacy ${contentClassName}`.trim()}>
            {children}
          </section>
        </main>
      </div>

      <COffcanvas placement="start" visible={mobileNavOpen} onHide={() => setMobileNavOpen(false)} className="admin-core-offcanvas">
        <COffcanvasHeader closeButton className="admin-core-offcanvas__header">
          <div className="admin-core-offcanvas__title">{workspaceTitle}</div>
        </COffcanvasHeader>
        <COffcanvasBody className="admin-core-offcanvas__body">
          {renderNavigation()}
        </COffcanvasBody>
      </COffcanvas>

      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="admin-core-search-dialog">
          <DialogHeader>
            <DialogTitle>{searchTitle}</DialogTitle>
            <DialogDescription>{searchDescription}</DialogDescription>
          </DialogHeader>
          <CFormInput
            type="search"
            className="admin-core-search-dialog__input"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            autoFocus
          />
          <div className="admin-core-search-dialog__results">
            {filteredSearchItems.length ? filteredSearchItems.map((item) => (
              <button
                key={`${item.href}-${item.label}`}
                type="button"
                className="admin-core-search-dialog__item"
                onClick={() => goTo(item.href)}
              >
                <strong>{item.label}</strong>
                <span>{item.description || item.href}</span>
              </button>
            )) : (
              <div className="admin-core-search-dialog__empty">{searchEmptyText}</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
